/**
 * PoRepo sobre Supabase. Misma lógica que DexiePoRepo, incluida la
 * reconciliación automática del estado de la PR origen (VD §5.3 / §7.4).
 * La reconciliación usa composición con SupabasePrRepo.
 */

import type { PoRepo } from "../../repos/PoRepo";
import type { Po, PrHistorialEntry } from "@app-types/entities";
import type { EstadoPo, EstadoPr } from "@constants/estados";
import { puedeTransicionarPo } from "@/features/po/poRules";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";
import { SupabasePrRepo } from "./SupabasePrRepo";

function normalizar(po: Po): Po {
  if (Array.isArray(po.lineas)) return po;
  return { ...po, lineas: [] };
}

export class SupabasePoRepo extends SupabaseBaseRepo<Po> implements PoRepo {
  private prRepo: SupabasePrRepo;

  constructor(tenantId: string) {
    super("po", tenantId);
    this.prRepo = new SupabasePrRepo(tenantId);
  }

  /** Recalcula y aplica el estado coherente de la PR según sus POs vivas. */
  private async reconciliarEstadoPr(prId: string, userEmail: string): Promise<void> {
    const pr = await this.prRepo.get(prId);
    if (!pr || pr.estado === "CANCELADA") return;
    const posVivas = (await this.listAll()).filter(
      (p) => p.prId === prId && p.estado !== "CANCELADA",
    );
    const sumaPosVivasUsd = posVivas.reduce((s, p) => s + (p.montoUsd ?? 0), 0);
    const prMontoUsd = pr.montoUsd ?? 0;

    let nuevoEstado: EstadoPr | null = null;
    if (sumaPosVivasUsd <= 0) {
      if (pr.estado === "ADJ_PARCIAL" || pr.estado === "ADJ_TOTAL") nuevoEstado = "EE_ABAS";
    } else if (sumaPosVivasUsd + 0.01 >= prMontoUsd) {
      if (pr.estado !== "ADJ_TOTAL") nuevoEstado = "ADJ_TOTAL";
    } else {
      if (pr.estado !== "ADJ_PARCIAL") nuevoEstado = "ADJ_PARCIAL";
    }
    if (!nuevoEstado || nuevoEstado === pr.estado) return;

    const entry: PrHistorialEntry = {
      fecha: new Date().toISOString(),
      estadoAnterior: pr.estado,
      estadoNuevo: nuevoEstado,
      usuario: userEmail || "system",
      observaciones: `Auto-transición por cambio en POs derivadas (Σ vivas USD = ${sumaPosVivasUsd.toFixed(2)} de ${prMontoUsd.toFixed(2)})`,
    };
    const partial: Partial<typeof pr> = {
      estado: nuevoEstado,
      historial: [...(pr.historial ?? []), entry],
      ...(nuevoEstado === "ADJ_PARCIAL" && !pr.fechaEmisionPo
        ? { fechaEmisionPo: new Date().toISOString().slice(0, 10) }
        : {}),
      ...(nuevoEstado === "ADJ_TOTAL" && !pr.fechaAdjudicacionCompleta
        ? { fechaAdjudicacionCompleta: new Date().toISOString().slice(0, 10) }
        : {}),
    };
    await this.prRepo.update(prId, partial, pr.version);
  }

  async list(filter?: Partial<Po>): Promise<Po[]> {
    const all = (await this.listAll()).map(normalizar);
    if (filter?.estado) return all.filter((p) => p.estado === filter.estado);
    if (filter?.proveedorId) return all.filter((p) => p.proveedorId === filter.proveedorId);
    return all.sort((a, b) => b.fechaEmision.localeCompare(a.fechaEmision));
  }

  async listByProveedor(proveedorId: string): Promise<Po[]> {
    return (await this.listAll()).filter((p) => p.proveedorId === proveedorId).map(normalizar);
  }

  async listByEstado(estado: EstadoPo): Promise<Po[]> {
    return (await this.listAll()).filter((p) => p.estado === estado).map(normalizar);
  }

  async listByPrId(prId: string): Promise<Po[]> {
    return (await this.listAll()).filter((p) => p.prId === prId).map(normalizar);
  }

  async get(id: string): Promise<Po | null> {
    const item = await this.getById(id);
    return item ? normalizar(item) : null;
  }

  async create(data: Omit<Po, "id" | "createdAt" | "updatedAt" | "version">): Promise<string> {
    const now = new Date().toISOString();
    const nuevo: Po = {
      ...data,
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    await this.createRaw(nuevo);
    if (nuevo.prId) await this.reconciliarEstadoPr(nuevo.prId, nuevo.createdBy ?? "system");
    return nuevo.id;
  }

  async update(id: string, data: Partial<Po>, expectedVersion: number): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`PO ${id} not found`);
    const updated: Po = {
      ...current,
      ...data,
      // Auditoria de creacion INMUTABLE: ver BaseRepo.update.
      createdBy: current.createdBy,
      createdAt: current.createdAt,
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
    };
    await this.updateRaw(updated, expectedVersion);
    const prsAReconciliar = new Set<string>();
    if (current.prId) prsAReconciliar.add(current.prId);
    if (updated.prId) prsAReconciliar.add(updated.prId);
    const userEmail = (data.updatedBy as string) ?? current.updatedBy ?? "system";
    for (const prId of prsAReconciliar) await this.reconciliarEstadoPr(prId, userEmail);
  }

  async delete(id: string): Promise<void> {
    const current = await this.get(id);
    await this.deleteById(id);
    if (current?.prId) await this.reconciliarEstadoPr(current.prId, "system");
  }

  async changeState(id: string, nextState: EstadoPo, userEmail: string, observaciones?: string): Promise<void> {
    const po = await this.get(id);
    if (!po) throw new Error(`PO ${id} not found`);
    if (po.estado === nextState) return;
    if (!puedeTransicionarPo(po.estado, nextState)) {
      throw new Error(`Transicion ${po.estado} -> ${nextState} no permitida`);
    }
    const entry = {
      fecha: new Date().toISOString(),
      estadoAnterior: po.estado,
      estadoNuevo: nextState,
      usuario: userEmail || "system",
      ...(observaciones && observaciones.trim() ? { observaciones } : {}),
    };
    const historial = [...(po.historial ?? []), entry];
    const partial: Partial<Po> = { estado: nextState, historial };
    if (nextState === "CANCELADA" && observaciones && observaciones.trim()) {
      partial.motivoCancelacion = observaciones;
    }
    await this.update(id, partial, po.version);
  }
}
